import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { UserPlus } from "lucide-react";
import { ROUTES } from "@/lib/routes";
import { FollowButton } from "@/components/FollowButton";
import { UserAvatar } from "@/components/UserAvatar";
import { useAuth } from "@/hooks/useAuth";

interface UserJoinedCardProps {
  activity: {
    id: string;
    timestamp: string;
    userId: string;
    user?: {
      id: string;
      username: string;
      avatar_url?: string;
      featured_vehicle_photo_url?: string;
    };
    metadata?: {
      username?: string;
      avatar_url?: string;
    };
  };
}

export const UserJoinedCard = ({ activity }: UserJoinedCardProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activity.user?.id) {
      navigate(ROUTES.profile(activity.user.id));
    }
  };

  const username = activity.user?.username || activity.metadata?.username || "New User";
  const featuredVehiclePhotoUrl = activity.user?.featured_vehicle_photo_url;
  const avatarUrl = featuredVehiclePhotoUrl || activity.user?.avatar_url || activity.metadata?.avatar_url;
  const targetUserId = activity.user?.id || activity.userId;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div 
            className="relative cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleUserClick}
          >
            <UserAvatar
              username={username}
              avatarUrl={avatarUrl}
              size="lg"
              usePhotoFilter={!!featuredVehiclePhotoUrl}
            />
            <div className="absolute -bottom-1 -right-1 h-6 w-6 bg-primary rounded-full flex items-center justify-center">
              <UserPlus className="h-3.5 w-3.5 text-primary-foreground" />
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm">
                <span 
                  className="font-semibold cursor-pointer hover:underline"
                  onClick={handleUserClick}
                >
                  {username}
                </span>
                {" "}joined Airing Down
              </p>
              <FollowButton 
                userId={targetUserId} 
                currentUserId={user?.id}
                size="sm"
                className="h-7 text-xs"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
